import React, { useState } from 'react';
import { SearchIcon, FilterIcon, PlusIcon, EditIcon, TrashIcon } from 'lucide-react';
// Sample participant data
const sampleParticipants = [{
  id: 'P001',
  name: 'National Bank of Ethiopia',
  shortName: 'NBE',
  bic: 'NBETETAA',
  status: 'Active',
  type: 'Bank',
  activationDate: '2023-01-15'
}, {
  id: 'P002',
  name: 'Commercial Bank of Ethiopia',
  shortName: 'CBE',
  bic: 'CBETETAA',
  status: 'Active',
  type: 'Bank',
  activationDate: '2023-02-10'
}, {
  id: 'P003',
  name: 'Dashen Bank',
  shortName: 'DB',
  bic: 'DASHETA',
  status: 'Active',
  type: 'Bank',
  activationDate: '2023-03-05'
}, {
  id: 'P004',
  name: 'Telebirr',
  shortName: 'TB',
  bic: 'TELEBIR',
  status: 'Active',
  type: 'MSP',
  activationDate: '2023-04-20'
}, {
  id: 'P005',
  name: 'Amole',
  shortName: 'AM',
  bic: 'AMOLEET',
  status: 'In-Active',
  type: 'Aggregator',
  activationDate: '2023-05-12'
}];
interface ParticipantsListProps {
  onNavigate?: (path: string) => void;
}
export function ParticipantsList({
  onNavigate
}: ParticipantsListProps = {}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const filteredParticipants = sampleParticipants.filter(participant => {
    // Apply search filter
    const matchesSearch = participant.name.toLowerCase().includes(searchTerm.toLowerCase()) || participant.shortName.toLowerCase().includes(searchTerm.toLowerCase()) || participant.bic.toLowerCase().includes(searchTerm.toLowerCase()) || participant.id.toLowerCase().includes(searchTerm.toLowerCase());
    // Apply status filter
    const matchesStatus = statusFilter === 'All' || participant.status === statusFilter;
    // Apply type filter
    const matchesType = typeFilter === 'All' || participant.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });
  // Navigate to add participant page
  const handleAddParticipant = () => {
    if (onNavigate) {
      // Use the provided navigation function if available
      onNavigate('participants/add');
    } else {
      // Fallback to hash-based navigation
      window.location.href = '#/participants/add';
    }
  };
  // Navigate to edit participant page with the participant id
  const handleEditParticipant = (participantId: string) => {
    // Store the participant ID in sessionStorage for retrieval in the edit page
    sessionStorage.setItem('editParticipantId', participantId);
    if (onNavigate) {
      // Use the provided navigation function if available
      onNavigate('participants/edit');
    } else {
      // Fallback to hash-based navigation
      window.location.href = '#/participants/edit';
    }
  };
  return <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-830">
      <div className="flex justify-between items-center mb-6" data-id="element-831">
        <h1 className="text-2xl font-semibold text-[#1A1F36]" data-id="element-832">
          Participants List
        </h1>
        <button className="flex items-center bg-[#F58220] text-white px-4 py-2 rounded-md hover:bg-[#e07619] transition-colors" onClick={handleAddParticipant} data-id="element-833">
          <PlusIcon size={18} className="mr-2" data-id="element-834" />
          Add Participant
        </button>
      </div>
      <div className="flex flex-col md:flex-row gap-4 mb-6" data-id="element-835">
        <div className="flex-grow" data-id="element-836">
          <div className="relative" data-id="element-837">
            <input type="text" placeholder="Search participants..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-838" />
            <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-839" />
          </div>
        </div>
        <div className="flex space-x-4" data-id="element-840">
          <div className="w-40" data-id="element-841">
            <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-842">
              <option value="All" data-id="element-843">All Status</option>
              <option value="Active" data-id="element-844">Active</option>
              <option value="In-Active" data-id="element-845">In-Active</option>
              <option value="Closed" data-id="element-846">Closed</option>
            </select>
          </div>
          <div className="w-40" data-id="element-847">
            <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} data-id="element-848">
              <option value="All" data-id="element-849">All Types</option>
              <option value="Bank" data-id="element-850">Bank</option>
              <option value="MSP" data-id="element-851">MSP</option>
              <option value="Aggregator" data-id="element-852">Aggregator</option>
            </select>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto" data-id="element-853">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-854">
          <thead className="bg-gray-50" data-id="element-855">
            <tr data-id="element-856">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-857">
                Participant Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-858">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-859">
                Short Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-860">
                BIC
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-861">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-862">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-863">
                Activation Date
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-864">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-865">
            {filteredParticipants.length > 0 ? filteredParticipants.map(participant => <tr key={participant.id} className="hover:bg-gray-50" data-id="element-866">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-867">
                    {participant.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-868">
                    {participant.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-869">
                    {participant.shortName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-870">
                    {participant.bic}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-871">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${participant.status === 'Active' ? 'bg-green-100 text-green-800' : participant.status === 'In-Active' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`} data-id="element-872">
                      {participant.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-873">
                    {participant.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-874">
                    {participant.activationDate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-875">
                    <button className="text-[#F58220] hover:text-[#e07619] mr-3" title="Edit" onClick={() => handleEditParticipant(participant.id)} data-id="element-876">
                      <EditIcon size={18} data-id="element-877" />
                    </button>
                    <button className="text-red-500 hover:text-red-700" title="Delete" onClick={() => {}} data-id="element-878">
                      <TrashIcon size={18} data-id="element-879" />
                    </button>
                  </td>
                </tr>) : <tr data-id="element-880">
                <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-881">
                  No participants found matching the criteria
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
    </div>;
}